<template>
  <div class="coupon_detail_popup" @click.stop="onBackdropClick">
    <div
      ref="detailSheet"
      class="coupon_detail_sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coupon_detail_title"
      tabindex="-1"
    >
      <div class="row_center_between coupon_detail_head">
        <div id="coupon_detail_title" class="coupon_detail_title">優惠券詳情</div>
        <button
          ref="closeButton"
          type="button"
          class="coupon_detail_close"
          aria-label="關閉詳情"
          @click="close"
        >
          ×
        </button>
      </div>
      <div class="coupon_detail_body">
        <couponLoading v-if="isLoading" text="正在載入優惠詳情…" />
        <div class="coupon_detail_state" v-else-if="hasLoadError">
          優惠詳情載入失敗，請稍後再試
          <button type="button" class="coupon_detail_retry" @click="load">
            重新載入
          </button>
        </div>
        <template v-else-if="getMemberCoupon">
          <h3 class="coupon_detail_name">{{ getTitle }}</h3>
          <div class="coupon_detail_card">
            <template v-if="getDescription">
              <h4>優惠說明</h4>
              <p>{{ getDescription }}</p>
            </template>
            <h4>使用門檻</h4>
            <p v-for="(text, index) in getPublicRuleDetail.thresholdTexts" :key="`threshold-${index}`">
              {{ text }}
            </p>
            <h4>優惠內容</h4>
            <p v-for="(text, index) in getPublicRuleDetail.benefitTexts" :key="`benefit-${index}`">
              {{ text }}
            </p>
            <h4>領取時間</h4>
            <p>{{ getClaimedAtText }}</p>
            <h4>使用期限</h4>
            <p>{{ getValidPeriodText }}</p>
          </div>
        </template>
      </div>
      <div class="coupon_detail_foot">
        <button type="button" class="coupon_detail_back" @click="close">
          返回選券列表
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { formatCouponDateTime, getCouponValidFromAt, getCouponValidUntilAt } from "@/utils/coupon.utils";
import { getCouponPublicRuleDetail } from "@/utils/couponTemplate.utils";
import { getCouponTargetText } from "@/utils/couponCheckout.utils";
import couponLoading from "@/components/coupon/couponLoading.vue";

export default {
  components: { couponLoading },
  props: {
    memberCouponUuid: { type: String, required: true },
    fallbackTitle: { type: String, default: "優惠券" },
  },
  emits: ["close"],
  data() {
    return {
      isLoadingDetail: true,
      isDetailActive: true,
      openedMemberUuid: this.$store.getters["userData/storeMemberUuid"],
      openedStoreCode: this.$store.getters["userData/storeCode"],
    };
  },
  computed: {
    getStoreMemberUuid() {
      return this.$store.getters["userData/storeMemberUuid"];
    },
    getStoreCode() {
      return this.$store.getters["userData/storeCode"];
    },
    isCurrentIdentity() {
      return (
        this.isDetailActive &&
        this.openedMemberUuid === this.getStoreMemberUuid &&
        this.openedStoreCode === this.getStoreCode
      );
    },
    getDetailState() {
      const state = this.$store.getters["coupon/memberCouponDetailV2"];
      return this.isCurrentIdentity && state?.uuid === this.memberCouponUuid
        ? state
        : null;
    },
    getMemberCoupon() {
      const data = this.getDetailState?.data;
      return data?.member_coupon_uuid === this.memberCouponUuid ? data : null;
    },
    isLoading() {
      return this.isLoadingDetail || Boolean(this.getDetailState?.loading);
    },
    hasLoadError() {
      return !this.isLoading && (Boolean(this.getDetailState?.error) || !this.getMemberCoupon);
    },
    getTitle() {
      return (
        this.getMemberCoupon?.current_public_rules?.title ||
        this.getMemberCoupon?.title ||
        this.fallbackTitle ||
        "優惠券"
      );
    },
    getDescription() {
      return this.getMemberCoupon?.current_public_rules?.description ?? "";
    },
    getPublicRuleDetail() {
      const publicRules = this.getMemberCoupon?.current_public_rules;
      const detail = getCouponPublicRuleDetail({
        publicRules,
      });
      if (publicRules?.benefit_scope === "target"
        && Array.isArray(publicRules.benefit_targets)
        && publicRules.benefit_targets.length === 1) {
        const target = publicRules.benefit_targets[0];
        const targetText = target && ["product", "combo", "spec_option"].includes(target.target_type)
          ? getCouponTargetText({ target }) : "指定商品或規格";
        detail.benefitTexts = [detail.benefitTexts[0].replace(/指定(?:商品|套餐)/, () => targetText)];
      }
      return detail;
    },
    getClaimedAtText() {
      return this.getDateText(this.getMemberCoupon?.claimed_at);
    },
    getValidPeriodText() {
      return `${this.getDateText(getCouponValidFromAt(this.getMemberCoupon))} ～ ${this.getDateText(getCouponValidUntilAt(this.getMemberCoupon))}`;
    },
  },
  watch: {
    getStoreMemberUuid() {
      this.close();
    },
    getStoreCode() {
      this.close();
    },
  },
  mounted() {
    this.load();
    document.addEventListener("keydown", this.onKeydown, true);
    this.$nextTick(() => this.$refs.closeButton?.focus());
  },
  beforeUnmount() {
    this.isDetailActive = false;
    document.removeEventListener("keydown", this.onKeydown, true);
    this.$store.dispatch("coupon/clearV2Detail", "memberCouponDetail");
  },
  methods: {
    async load() {
      if (!this.isCurrentIdentity) return;
      this.isLoadingDetail = true;
      try {
        await this.$store.dispatch("coupon/fetchMemberCouponDetailV2", this.memberCouponUuid);
      } finally {
        if (this.isDetailActive) this.isLoadingDetail = false;
      }
    },
    getDateText(value) {
      return formatCouponDateTime(value, this.getMemberCoupon?.timezone_name) || "未提供";
    },
    onBackdropClick(event) {
      if (event.target === event.currentTarget) this.close();
    },
    onKeydown(event) {
      if (!this.isDetailActive) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close();
      } else if (event.key === "Tab") {
        const sheet = this.$refs.detailSheet;
        const buttons = sheet?.querySelectorAll("button:not(:disabled)");
        if (!buttons?.length) return;
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (!sheet.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    },
    close() {
      if (!this.isDetailActive) return;
      this.isDetailActive = false;
      this.$emit("close");
    },
  },
};
</script>
<style lang="scss" scoped>
.coupon_detail_popup {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.coupon_detail_sheet {
  width: 100%;
  max-width: 600px;
  max-height: 86vh;
  background-color: #fff;
  border-radius: 16px 16px 0 0;
  padding: 18px 16px calc(env(safe-area-inset-bottom) + 16px);
  display: flex;
  flex-direction: column;
}

.coupon_detail_head {
  align-items: flex-start;
  margin-bottom: 10px;
  gap: 12px;
}

.coupon_detail_title {
  color: #fd5001;
  font-size: 19px;
  font-weight: 600;
}

.coupon_detail_close {
  border: none;
  background: none;
  font-size: 22px;
  line-height: 1;
  color: #999;
}

.coupon_detail_body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 14px;
}

.coupon_detail_name {
  font-size: 19px;
  font-weight: 600;
  margin: 4px 0 12px;
  overflow-wrap: anywhere;
}

.coupon_detail_card {
  padding: 14px 16px;
  background-color: #fafafa;
  border: 1px solid #eee;
  border-radius: 10px;

  h4 {
    margin: 14px 0 4px;
    font-size: 15px;
    font-weight: 600;

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    margin: 4px 0;
    font-size: 14px;
    overflow-wrap: anywhere;
  }
}

.coupon_detail_state {
  padding: 40px 10px;
  text-align: center;
  color: #888;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.coupon_detail_retry {
  margin-top: 12px;
  border: 1px solid #fd5001;
  color: #fd5001;
  background-color: #fff;
  border-radius: 8px;
  padding: 6px 18px;
}

.coupon_detail_foot {
  padding-top: 10px;
  border-top: 1px solid #eee;
}

.coupon_detail_back {
  width: 100%;
  border: none;
  background-color: #facb06;
  border-radius: 8px;
  padding: 10px 0;
  font-weight: 600;
}
</style>
